import { useUpdateLineItem } from '@/api/api-mutations'
import { PaymentProviderType } from '@/api/common-types'
import {
	AdyenEcomData,
	OrderInitiatedPaymentInfoBody,
	PaymentInfoTransaction,
	VerifoneCheckoutData,
	VerifoneEcomData
} from '@/api/orders-types'
import { useApiContext, useGetPaymentProviders } from '@/api/react-query'
import { PaymentProvider, StoreOrderStatus } from '@/api/store-orders-types'
import { PaymentState, TransactionIdentifier, usePaymentHelper } from '@/business-logic/payment-helper'
import { AdyenCheckoutComponent } from '@/components/adyen-checkout'
import { ErrorDisplay } from '@/components/error-display'
import { LoadingSpinner } from '@/components/loading-spinner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Image } from '@/components/ui/image'
import { VerifoneCheckoutComponent } from '@/components/verifone-checkout'
import { orderIdSubject, useGetCurrentOrder, useOrderContext } from '@/contexts/order-context'
import { useI18n } from '@/i18n/i18n-react'
import { tenant } from '@/tenants/tenant'
import { useGetSubject, useSubject } from '@/utils/hooks'
import { StoredObjectSubject } from '@/utils/storage'
import { isDevelopment } from '@/utils/utilities'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { Minus, Plus, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { ulid } from 'ulid'

const transactionIdentifierSubject = new StoredObjectSubject<TransactionIdentifier | null>(
	'transaction-identifier',
	null
)

export const Route = createFileRoute('/cart')({
	component: Cart,
	beforeLoad: () => {
		if (!tenant.features.ecomEnabled) {
			throw redirect({ to: '/' })
		}
	}
	// loader: ({ context: { queryClient } }) => {
	// 	return cartIdSubject.value ?
	// 			queryClient.ensureQueryData(constructGetCartQuery(cartIdSubject.value))
	// 		:	Promise.resolve()
	// }
})

export default function Cart() {
	const { L } = useI18n()
	const order = useGetCurrentOrder()
	const orderUuid = useGetSubject(orderIdSubject)
	const navigate = useNavigate()
	const { resetOrder } = useOrderContext()

	const [errorMessage, setErrorMessage] = useState('')

	const [adyenModalOpen, adyenModalOpenSubject] = useSubject(false)
	const [adyenEcomData, setAdyenEcomData] = useState<AdyenEcomData | null>(null)

	const [verifoneModalOpen, verifoneModalOpenSubject] = useSubject(false)
	const [verifoneCheckoutData, setVerifoneCheckoutData] = useState<VerifoneCheckoutData | null>(null)
	const [verifoneEcomData, setVerifoneEcomData] = useState<VerifoneEcomData | null>(null)

	useEffect(() => {
		// Clear carts that are Completed or Canceled
		if (order?.data?.status && order.data.status !== StoreOrderStatus.Draft) {
			resetOrder()
		}
	}, [order.data?.status])

	const paymentCompleted = useCallback(() => {
		resetOrder()
		navigate({ to: '/receipt/$receiptId', params: { receiptId: orderUuid } })
	}, [orderUuid])

	const { api } = useApiContext()
	const { data: paymentProviders } = useGetPaymentProviders()
	const updateLineItem = useUpdateLineItem()

	const transactionIdentifier = useGetSubject(transactionIdentifierSubject)

	// useEffect(() => {
	// 	setErrorMessage(pollOrderPaymentInfoError ? L.Cart.PaymentInfoCouldNotBeRetrieved() : '')
	// }, [pollOrderPaymentInfoError])

	const onProviderDataReady = useCallback(async (transaction: PaymentInfoTransaction) => {
		switch (transaction.provider) {
			case PaymentProvider.Cash:
				break
			case PaymentProvider.AdyenEcom:
				if (transaction.adyenData) {
					setAdyenEcomData(transaction.adyenData)
					adyenModalOpenSubject.next(true)
				}
				break
			case PaymentProvider.VerifoneCheckout:
				if (transaction.verifoneData) {
					setVerifoneCheckoutData(transaction.verifoneData)
					verifoneModalOpenSubject.next(true)
				}
				break
			case PaymentProvider.VerifoneEcom:
				if (transaction.verifoneData) {
					setVerifoneEcomData(transaction.verifoneData)
					verifoneModalOpenSubject.next(true)
				}
				break
			default:
				throw new Error(`Unsupported payment provider: ${transaction.provider}`)
		}
	}, [])

	const { initiatePayment, paymentState, updateTransactionState, cancelPayment } = usePaymentHelper({
		orderUuid,
		transactionIdentifierSubject,
		onProviderDataReady,
		onError: setErrorMessage,
		onCompleted: paymentCompleted
	})

	const handlePayment = async (provider: PaymentProviderType) => {
		const clientReference = ulid()
		let body: OrderInitiatedPaymentInfoBody
		switch (provider) {
			case PaymentProvider.AdyenEcom:
				body = {
					clientReference,
					provider,
					returnUrl: location.href
				}
				break
			case PaymentProvider.VerifoneCheckout:
				body = {
					clientReference,
					provider,
					method: { type: 'VERIFONE_CHECKOUT_CARD' as const }
				}
				break
			case PaymentProvider.VerifoneEcom:
				body = {
					clientReference,
					provider,
					method: { type: 'VERIFONE_ECOM_CARD_CAPTURE' as const }
				}
				break
			case PaymentProvider.Cash:
				if (!isDevelopment) {
					throw new Error('Cash payment is not supported in production')
				}
				body = {
					clientReference,
					provider: 'CASH' as const,
					method: 'CASH_INSTANT' as const
				}
				break
			case PaymentProvider.Cash:
				if (!isDevelopment) {
					throw new Error('Cash payment is not supported in production')
				}
				body = {
					clientReference,
					provider: 'CASH' as const,
					method: 'CASH_ASYNC' as const
				}
				break
			default:
				throw new Error(`Unsupported payment provider: ${provider}`)
				break
		}

		initiatePayment(body)
	}

	const handleAdyenPaymentResult = (result: { resultCode: string; [key: string]: any }) => {
		adyenModalOpenSubject.next(false)
		setAdyenEcomData(null)
		updateTransactionState('COMPLETED')
		// Payment completed successfully - the polling will handle the rest
	}

	const handleAdyenError = (error: any) => {
		console.error('Adyen payment error:', error)
		adyenModalOpenSubject.next(false)
		setAdyenEcomData(null)
		setErrorMessage(L.Cart.PaymentFailed())
		cancelPayment()
	}

	const handleVerifonePaymentResult = (result: { resultCode: string; [key: string]: any }) => {
		console.log('Verifone payment result:', result)
		verifoneModalOpenSubject.next(false)
		setVerifoneCheckoutData(null)
		setVerifoneEcomData(null)
		updateTransactionState('COMPLETED')
		// Payment completed successfully - the polling will handle the rest
	}

	const handleVerifoneError = (error: any) => {
		console.error('Verifone payment error:', error)
		verifoneModalOpenSubject.next(false)
		setVerifoneCheckoutData(null)
		setVerifoneEcomData(null)
		setErrorMessage(L.Cart.PaymentFailed())
		cancelPayment()
	}

	const items = order.data?.itemGroups?.flatMap(group => group.items) ?? []

	const isProcessingOrLoading = order.isLoading || paymentState !== PaymentState.NotStarted
	console.log('Render complete', { isProcessingOrLoading, orderLoading: order.isLoading, paymentState })
	return (
		<div className='container mx-auto max-w-4xl p-4 sm:p-6'>
			{/* Payment Processing Overlay */}
			{isProcessingOrLoading && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
					<Card className='p-6 text-center sm:p-8'>
						<div className='flex flex-col items-center space-y-4'>
							<LoadingSpinner />
							<h3 className='text-lg font-semibold'>{L.Cart.ProcessingPayment(paymentState)}</h3>
							<p className='text-muted-foreground'>{L.Cart.PleaseWait(paymentState)}</p>
							<Button
								variant='outline'
								onClick={cancelPayment}
								disabled={paymentState === PaymentState.Cancelling}
							>
								{L.Common.Actions.Cancel()}
							</Button>
						</div>
					</Card>
				</div>
			)}

			<Helmet>
				<title>{L.Cart.Title()}</title>
			</Helmet>

			<h1 className='pb-6 text-2xl font-bold sm:pb-8 sm:text-3xl lg:text-4xl'>{L.Cart.Title()}</h1>

			{/* Payment error display */}
			{errorMessage && (
				<Card className='border-destructive bg-destructive/5 mb-6 p-4'>
					<div className='flex items-start space-x-3'>
						<div className='flex-1'>
							<h4 className='text-destructive font-semibold'>{L.Cart.PaymentError()}</h4>
							<p className='text-destructive/80 text-sm'>{errorMessage}</p>
						</div>
						<Button
							variant='ghost'
							size='sm'
							onClick={() => setErrorMessage('')}
							aria-label='Dismiss error'
						>
							<X className='h-4 w-4' />
						</Button>
					</div>
				</Card>
			)}

			{order.error ?
				<ErrorDisplay refetch={order.refetch} className='mb-6' />
			: items.length === 0 ?
				<Card className='p-6 sm:p-8 lg:p-12'>
					<div className='space-y-4 text-center'>
						<p className='text-muted-foreground text-lg sm:text-xl'>{L.Cart.Empty()}</p>
						<Button asChild size='lg'>
							<Link to='/'>{L.Cart.ContinueShopping()}</Link>
						</Button>
					</div>
				</Card>
			:	<>
					<div className='space-y-4'>
						{items.map(item => {
							return (
								<Card key={item.uuid} className='group transition-shadow hover:shadow-lg'>
									<div className='flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:p-6'>
										<div className='mx-auto shrink-0 rounded-lg bg-gray-50 p-2 sm:mx-0'>
											<Image
												src={item.variantImageUrl}
												alt={item.productTitle}
												className='aspect-square h-20 w-20 object-contain sm:h-24 sm:w-24 lg:h-28 lg:w-28'
											/>
										</div>
										<div className='flex-grow text-center sm:px-4 sm:text-left lg:px-8'>
											<h2 className='mb-2 text-lg font-semibold sm:text-xl'>
												{item.productTitle}
											</h2>
											<div className='space-y-1'>
												<p className='text-muted-foreground text-sm'>
													{L.Amount.Default({
														amount: item.unitPrice?.gross,
														currencyCode: order.data?.currencyCode
													})}
												</p>
												<p className='text-primary text-lg font-bold sm:text-xl'>
													{L.Amount.Default({
														amount: item.totals.gross,
														currencyCode: order.data?.currencyCode
													})}
												</p>
											</div>
										</div>
										<div className='flex flex-col items-center space-y-3 sm:items-end'>
											<Button
												size='icon'
												variant='ghost'
												className='hover:bg-destructive/10 hover:text-destructive h-8 w-8 opacity-60 transition-opacity hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100'
												onClick={() =>
													updateLineItem.mutateAsync({
														path: {
															orderUuid,
															lineItemUuid: item.uuid
														},
														body: {
															removeFromOrder: true
														}
													})
												}
												aria-label='Remove item'
											>
												<Trash2 className='h-4 w-4' />
											</Button>
											<div className='bg-secondary/10 flex items-center space-x-2 rounded-lg px-2 py-1 sm:space-x-3 sm:px-3 sm:py-1.5'>
												<Button
													size='icon'
													variant='ghost'
													className='hover:bg-secondary/20 h-6 w-6 sm:h-8 sm:w-8'
													onClick={() =>
														updateLineItem.mutateAsync({
															path: {
																orderUuid,
																lineItemUuid: item.uuid
															},
															body: {
																quantityChange: -1
															}
														})
													}
												>
													<Minus className='h-4 w-4' />
												</Button>
												<span className='w-6 text-center text-sm font-medium sm:w-8'>
													{item.quantityInfo.quantity}
												</span>
												<Button
													size='icon'
													variant='ghost'
													className='hover:bg-secondary/20 h-6 w-6 sm:h-8 sm:w-8'
													onClick={() =>
														updateLineItem.mutateAsync({
															path: {
																orderUuid,
																lineItemUuid: item.uuid
															},
															body: {
																quantityChange: 1
															}
														})
													}
												>
													<Plus className='h-4 w-4' />
												</Button>
											</div>
										</div>
									</div>
								</Card>
							)
						})}
					</div>
					<Card className='mt-8 overflow-hidden'>
						<div className='bg-secondary/5 p-8'>
							<div className='flex flex-col items-end space-y-4'>
								<div className='text-right'>
									<p className='text-muted-foreground mb-1 text-sm'>{L.Cart.Total()}</p>
									<p className='text-primary text-3xl font-bold'>
										{L.Amount.Default({
											amount: order.data?.totals.gross,
											currencyCode: order.data?.currencyCode
										})}
									</p>
								</div>
								<div className='flex flex-row flex-wrap justify-end gap-2'>
									{isDevelopment && (
										<>
											<Button
												className='px-8'
												size='lg'
												disabled={isProcessingOrLoading}
												onClick={() => handlePayment(PaymentProvider.Cash)}
											>
												{L.Cart.PayWith(PaymentProvider.CashAsync)}
											</Button>
											<Button
												className='px-8'
												size='lg'
												disabled={isProcessingOrLoading}
												onClick={() => handlePayment(PaymentProvider.Cash)}
											>
												{L.Cart.PayWith(PaymentProvider.Cash)}
											</Button>
										</>
									)}
									{paymentProviders?.some(p => p.provider === PaymentProvider.AdyenEcom) && (
										<Button
											className='px-8'
											size='lg'
											disabled={isProcessingOrLoading}
											onClick={() => handlePayment(PaymentProvider.AdyenEcom)}
										>
											{L.Cart.PayWith(PaymentProvider.AdyenEcom)}
										</Button>
									)}
									{paymentProviders?.some(p => p.provider === PaymentProvider.VerifoneCheckout) && (
										<Button
											className='px-8'
											size='lg'
											disabled={isProcessingOrLoading}
											onClick={() => handlePayment(PaymentProvider.VerifoneCheckout)}
										>
											{L.Cart.PayWith(PaymentProvider.VerifoneCheckout)}
										</Button>
									)}
									{paymentProviders?.some(p => p.provider === PaymentProvider.VerifoneEcom) && (
										<Button
											className='px-8'
											size='lg'
											disabled={isProcessingOrLoading}
											onClick={() => handlePayment(PaymentProvider.VerifoneEcom)}
										>
											{L.Cart.PayWith(PaymentProvider.VerifoneEcom)}
										</Button>
									)}
								</div>
							</div>
						</div>
					</Card>
				</>
			}

			{adyenEcomData && (
				<AdyenCheckoutComponent
					title={L.Cart.PayWith(PaymentProvider.AdyenEcom)}
					isOpen={adyenModalOpen}
					onClose={() => {
						adyenModalOpenSubject.next(false)
						cancelPayment()
					}}
					adyenData={adyenEcomData}
					onPaymentResult={handleAdyenPaymentResult}
					onError={handleAdyenError}
				/>
			)}
			{verifoneCheckoutData && (
				<VerifoneCheckoutComponent
					title={L.Cart.PayWith(PaymentProvider.VerifoneCheckout)}
					isOpen={verifoneModalOpen}
					onClose={() => {
						verifoneModalOpenSubject.next(false)
						cancelPayment()
					}}
					verifoneData={verifoneCheckoutData}
				/>
			)}
			{verifoneEcomData && verifoneEcomData.method === 'VERIFONE_ECOM_CARD_CAPTURE' && (
				<VerifoneCheckoutComponent
					title={L.Cart.PayWith(PaymentProvider.VerifoneEcom)}
					isOpen={verifoneModalOpen}
					onClose={() => {
						verifoneModalOpenSubject.next(false)
						cancelPayment()
					}}
					verifoneData={verifoneEcomData}
				/>
			)}
		</div>
	)
}
